import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useBackOrder } from '../../hooks/useBackOrder';
import { useStoreCredits } from '../../hooks/useStoreCredits';
import { formatCurrency } from '../../utils/formatters';
import { apiService, tokenManager, imageHelpers } from '../../services/api';
import CartOffcanvas from '../cart/CartOffcanvas';
import BackOrderOffcanvas from '../backorder/BackOrderOffcanvas';
import Navigation from './Navigation';
import StoreSwitcher from '../client/StoreSwitcher';
import CompanySwitcher from '../client/CompanySwitcher';
import UserFeedback from '@/components/common/UserFeedback';
import logoDark from '@/assets/images/logo-dark.png';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    store: currentStore,
    hasMultipleStores,
    canProceedToShopping,
    getUserDisplayName,
    isEmployee,
    isBrandUser,
    hasMultipleCompanies
  } = useAuth();
  
  const { 
    getCartTotal, 
    getCartItemCount, 
    canUseCart,
    isLoading: cartLoading,
    hasValidationErrors,
    userFeedback,
    clearUserFeedback
  } = useCart();

  const { backOrderItemCount, canUseBackOrder } = useBackOrder();
  const { enabled: creditsEnabled, availableTotal: creditsAvailable } = useStoreCredits();

  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [brandBooks, setBrandBooks] = useState([]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileMenu]);

  // Handle scroll behavior
  useEffect(() => {
    const headerElement = document.querySelector('.stash-header');
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          
          // Use different thresholds for showing/hiding to prevent jitter
          const shouldBeScrolled = isScrolled 
            ? scrollTop > 30  // Once scrolled, stay scrolled until 30px (lower threshold)
            : scrollTop > 50; // Initially, wait until 50px to scroll
          
          // Measure header height before it becomes fixed
          if (!isScrolled && shouldBeScrolled && headerElement) {
            setHeaderHeight(headerElement.offsetHeight);
          }
          
          setIsScrolled(shouldBeScrolled);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    // Initial measurement
    if (headerElement) {
      setHeaderHeight(headerElement.offsetHeight);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Fetch brand books from API
  useEffect(() => {
    const fetchBrandBooks = async () => {
      try {
        const company = tokenManager.getCurrentCompany();
        if (!company?.id) return;

        const response = await apiService.get('/storefront/BrandBooks', {
          $filter: `CompanyID eq ${company.id}`,
          $orderby: 'BrandBookName asc'
        });

        if (response?.value) {
          setBrandBooks(response.value);
        }
      } catch (err) {
        console.error('Failed to fetch brand books:', err);
      }
    };

    fetchBrandBooks();
  }, []);

  // Don't show header on login/register pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  const handleLogout = () => {
    logout();
    setShowMobileMenu(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowMobileSearch(false);
    }
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    if (!showMobileMenu) {
      setShowMobileSearch(false);
    }
  };

  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (!showMobileSearch) {
      setShowMobileMenu(false);
    }
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  // Cart values - use safe defaults
  const cartTotal = canUseCart ? getCartTotal() : 0;
  const cartItemCount = canUseCart ? getCartItemCount() : 0;
  // The store's spendable credit shows after the cart total; the search box gives up the room for it.
  const showCreditInHeader = creditsEnabled && creditsAvailable > 0;

  // For non-authenticated users, show a simpler header
  if (!isAuthenticated) {
    return (
      <>
        <UserFeedback 
          feedback={userFeedback}
          onClear={clearUserFeedback}
        />
        <header className={`stash-header ${isScrolled ? 'stash-header--scrolled' : ''}`}>
          <div className={`stash-header__banner d-none ${isScrolled ? 'stash-header__banner--hidden' : ''}`}>
            <div className="stash-header__banner-content">
              <Link to="/register" className="stash-header__banner-link">
                <span className="stash-header__banner-text">
                  Join Our Wholesale Program - Special Pricing Available
                </span>
              </Link>
            </div>
          </div>

          <div className="stash-header__main">
            <div className="container-fluid">
              <div className="row py-3">
                <div className="col-4 col-sm-4 col-lg-3 d-flex align-items-center justify-content-center justify-content-lg-start">
                  <div className="stash-header__logo">
                    <Link to="/" className="stash-header__logo-link">
                      <img 
                        src={logoDark}
                        alt="Stash House"
                        className="stash-header__logo-img img-fluid"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'inline';
                        }}
                      />
                      <span className="stash-header__logo-text">
                        {import.meta.env.VITE_APP_NAME || 'Stash House'}
                      </span>
                    </Link>
                  </div>
                </div>

                <div className="col-4 col-sm-4 col-lg-6 d-flex align-items-center justify-content-center">
                  <div className="stash-header__search">
                    <form onSubmit={handleSearch} className="d-none d-lg-block">
                      <div className="stash-header__search-group input-group">
                        <input
                          type="search"
                          className="stash-header__search-input form-control border-primary"
                          placeholder="Search products..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button className="stash-header__search-btn btn btn-stash-primary" type="submit">
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="col-4 col-sm-4 col-lg-3 d-flex align-items-center justify-content-end">
                  <div className="stash-header__actions">
                    <Link to="/login" className="stash-header__auth-btn stash-header__auth-btn--outline">
                      <i className="bi bi-person me-1"></i>
                      <span className="d-none d-md-inline">Sign In</span>
                    </Link>
                    <Link to="/register" className="stash-header__auth-btn stash-header__auth-btn--primary">
                      <span className="d-none d-md-inline">Register</span>
                      <i className="bi bi-person-plus d-md-none"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
      </>
    );
  }

  return (
    <>
      <UserFeedback 
        feedback={userFeedback}
        onClear={clearUserFeedback}
      />
      {/* Placeholder to prevent content jump when header becomes fixed */}
      {isScrolled && <div style={{ height: `${headerHeight}px` }} />}
      
      <header className={`stash-header ${isScrolled ? 'stash-header--scrolled' : ''}`}>
        {/* Mobile Top Bar - Store switcher for customers, Company switcher for employees */}
        {(isEmployee() ? hasMultipleCompanies() : hasMultipleStores()) && (
          <div className={`stash-header__mobile-topbar d-md-none ${isScrolled ? 'stash-header__mobile-topbar--hidden' : ''}`}>
            <div className="container-fluid">
              <div className="d-flex align-items-center py-2">
                {isEmployee() ? <CompanySwitcher variant="mobile" /> : <StoreSwitcher variant="mobile" />}
              </div>
            </div>
          </div>
        )}

        {/* Desktop Top banner with darker background */}
        <div className={`stash-header__banner d-none d-md-block ${isScrolled ? 'stash-header__banner--hidden' : ''}`}>
          <div className="container-fluid">
            <div className="row align-items-center justify-content-between">
              <div className="col-auto">
                <div className="stash-header__company-info">
                  {user && (
                    <span className="stash-header__user">
                      Welcome back, {getUserDisplayName()}!
                    </span>
                  )}
                  {isEmployee() && (
                    <span className="badge bg-info ms-2" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>
                      <i className="bi bi-eye me-1"></i>{isBrandUser() ? 'Brand Preview' : 'Employee Preview'}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-auto text-end">
                <div className="stash-header__nav-links">
                  {/* Labs link - hidden for brand users */}
                  {!isBrandUser() && (
                    <Link className="dropdown-item" to="/lab-reports">
                      <i className="bi bi-file-earmark-medical me-2"></i>
                      Labs
                    </Link>
                  )}
                  {/* Brand Books Dropdown - hidden for brand users */}
                  {!isBrandUser() && brandBooks.length > 0 && (
                    <div className="dropdown d-inline-block">
                      <button
                        className="stash-header__nav-link stash-header__nav-link--button dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="bi bi-book me-1"></i>
                        Brand Book{brandBooks.length > 1 ? 's' : ''}
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end">
                        {brandBooks.map((book) => (
                          <li key={book.id}>
                            <a
                              className="dropdown-item"
                              href={imageHelpers.getImageUrl(`/brandbooks/${book.brandBookLocation}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-file-pdf me-2"></i>
                              {book.brandBookName}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Store/Company Switcher */}
                  {isEmployee() ? (
                    hasMultipleCompanies() && <CompanySwitcher />
                  ) : (
                    canProceedToShopping() && hasMultipleStores() && <StoreSwitcher />
                  )}
                   <div className="dropdown d-inline-block">
                    <button
                      className="stash-header__nav-link stash-header__nav-link--button dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi bi-person-circle me-1"></i>
                      My Account
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to="/account">
                          <i className="bi bi-speedometer2 me-2"></i>
                          Dashboard
                        </Link>
                      </li>
                      {!isEmployee() && (
                        <li>
                          <Link className="dropdown-item" to="/orders">
                            <i className="bi bi-box-seam me-2"></i>
                            Store Orders
                          </Link>
                        </li>
                      )}
                      {!isEmployee() && canProceedToShopping() && hasMultipleStores() && (
                        <li>
                          <Link className="dropdown-item" to="/account/orders">
                            <i className="bi bi-shop me-2"></i>
                            All Orders
                          </Link>
                        </li>
                      )}
                      {!isEmployee() && creditsEnabled && (
                        <li>
                          <Link className="dropdown-item" to="/account/credits">
                            <i className="bi bi-wallet2 me-2"></i>
                            Credits
                          </Link>
                        </li>
                      )}
                      {!isEmployee() && (
                        <li>
                          <Link className="dropdown-item" to="/account/profile">
                            <i className="bi bi-person-gear me-2"></i>
                            Profile
                          </Link>
                        </li>
                      )}
                      <li><hr className="dropdown-divider" /></li>
                      <li>
                        <button className="dropdown-item text-danger" onClick={handleLogout}>
                          <i className="bi bi-box-arrow-right me-2"></i>
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main header section */}
        <div className="stash-header__main py-3">
          <div className="container-fluid">
            <div className="row py-3 align-items-center">
              {/* Logo - Left on mobile, left on desktop */}
              <div className="col col-lg-3 d-flex align-items-center">
                <div className="stash-header__logo">
                  <Link to="/" className="stash-header__logo-link">
                    <img
                      src={logoDark}
                      alt="Stash House"
                      className="stash-header__logo-img img-fluid"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'inline';
                      }}
                    />
                    <span className="stash-header__logo-text">
                      {import.meta.env.VITE_APP_NAME || 'Stash House'}
                    </span>
                  </Link>
                </div>
              </div>

              {/* Desktop search - hidden for brand users */}
              {!isBrandUser() && <div className={`${showCreditInHeader ? "col-lg" : "col-lg-6"} d-none d-lg-flex align-items-center`}>
                <div className="stash-header__search">
                  <form onSubmit={handleSearch} className="stash-header__search-form">
                    <div className="stash-header__search-group input-group input-group-lg">
                      <input
                        type="search"
                        className="stash-header__search-input form-control border-primary"
                        placeholder="Search products, brands, categories..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={!canProceedToShopping()}
                      />
                      <button
                        className="stash-header__search-btn btn btn-stash-primary"
                        type="submit"
                        disabled={!canProceedToShopping()}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    {!canProceedToShopping() && (
                      <small className="stash-header__search-help">
                        {isEmployee() ? 'Select a company to preview products' : 'Please select a store to search products'}
                      </small>
                    )}
                  </form>
                </div>
              </div>}

              {/* Right side actions */}
              <div className={`${showCreditInHeader ? "col-auto ms-auto" : "col-auto col-lg-3"} d-flex align-items-center justify-content-end`}>
                <div className="stash-header__actions">
                  {/* Mobile: Search icon - hidden for brand users */}
                  {!isBrandUser() && (
                    <button
                      className="stash-header__mobile-search-toggle d-lg-none"
                      onClick={toggleMobileSearch}
                      aria-label="Toggle search"
                    >
                      <i className="bi bi-search"></i>
                    </button>
                  )}

                  {/* Back Order button - sits before the cart, customers only */}
                  {!isEmployee() && canUseBackOrder && (
                    <div className="stash-header__cart">
                      <button
                        type="button"
                        className="py-0 d-flex align-items-center stash-header__cart-button stash-header__cart-button--active"
                        data-bs-toggle="offcanvas"
                        data-bs-target="#backOrderOffcanvas"
                        aria-controls="backOrderOffcanvas"
                        title="Back Order"
                        aria-label="Back Order"
                      >
                        <i className="bi bi-clock-history"></i>
                        {backOrderItemCount > 0 && (
                          <span className="stash-header__cart-badge">
                            {backOrderItemCount}
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Cart - hidden for employees */}
                  {!isEmployee() && <div className="stash-header__cart">
                    {location.pathname === '/checkout' ? (
                      <span
                        className="py-0 d-flex align-items-center stash-header__cart-button stash-header__cart-button--active"
                        style={{ cursor: 'default' }}
                      >
                        <i className="bi bi-cart-check"></i>
                        {canUseCart && cartItemCount > 0 && (
                          <span className="stash-header__cart-badge">
                            {cartItemCount}
                          </span>
                        )}
                      </span>
                    ) : (
                      <button
                        className={`py-0 d-flex align-items-center stash-header__cart-button ${
                          canUseCart ? 'stash-header__cart-button--active' : 'stash-header__cart-button--disabled'
                        } ${hasValidationErrors ? 'stash-header__cart-button--error' : ''}`}
                        data-bs-toggle="offcanvas"
                        data-bs-target="#cartOffcanvas"
                        aria-controls="cartOffcanvas"
                        disabled={!canUseCart}
                      >
                        {/* Cart loading indicator */}
                        {cartLoading ? (
                          <div className="stash-header__cart-loading">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <i className="bi bi-cart3"></i>
                        )}

                        {/* Cart count badge */}
                        {canUseCart && cartItemCount > 0 && (
                          <span className={`stash-header__cart-badge ${
                            hasValidationErrors ? 'stash-header__cart-badge--warning' : ''
                          }`}>
                            {cartItemCount}
                          </span>
                        )}
                      </button>
                    )}

                    {/* Cart info - desktop only */}
                    <div className="stash-header__cart-info d-none d-lg-block">
                      <div className="stash-header__cart-total">
                        {formatCurrency(cartTotal)}
                      </div>
                      <div className="stash-header__cart-count">
                        {cartItemCount} item{cartItemCount !== 1 ? 's' : ''}
                      </div>
                      {!canUseCart && (
                        <div className="stash-header__cart-help">
                          Select store to shop
                        </div>
                      )}
                      {hasValidationErrors && (
                        <div className="stash-header__cart-error">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Check items
                        </div>
                      )}
                    </div>

                    {/* Credit available for the current store - desktop only */}
                    {showCreditInHeader && (
                      <Link to="/account/credits" className="stash-header__credit-info d-none d-lg-block">
                        <div className="stash-header__credit-total">{formatCurrency(creditsAvailable)}</div>
                        <div className="stash-header__cart-count">credits</div>
                      </Link>
                    )}
                  </div>}

                  {/* Mobile: Hamburger menu */}
                  <button
                    className={`stash-header__hamburger d-lg-none ${showMobileMenu ? '' : 'collapsed'}`}
                    type="button"
                    onClick={toggleMobileMenu}
                    aria-expanded={showMobileMenu}
                    aria-label="Toggle navigation"
                  >
                    <span className="stash-header__hamburger-line"></span>
                    <span className="stash-header__hamburger-line"></span>
                    <span className="stash-header__hamburger-line"></span>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile search form - hidden for brand users */}
            {!isBrandUser() && <div className={`stash-header__mobile-search ${showMobileSearch ? 'stash-header__mobile-search--show' : ''}`}>
              <div className="stash-header__mobile-search-content">
                <form onSubmit={handleSearch}>
                  <div className="stash-header__search-group input-group input-group-lg">
                    <input
                      type="search"
                      className="stash-header__search-input form-control border-primary"
                      placeholder="Search products, brands, categories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={!canProceedToShopping()}
                    />
                    <button 
                      type="submit" 
                      className="stash-header__search-btn btn btn-stash-primary"
                      disabled={!canProceedToShopping()}
                    >
                      <i className="bi bi-search"></i>
                    </button>
                  </div>
                  {!canProceedToShopping() && (
                    <small className="stash-header__search-help">
                      {isEmployee() ? 'Select a company to preview products' : 'Please select a store to search products'}
                    </small>
                  )}
                </form>
              </div>
            </div>}
          </div>
        </div>

        {/* Mobile menu backdrop */}
        <div
          className={`stash-header__mobile-backdrop d-lg-none ${showMobileMenu ? 'stash-header__mobile-backdrop--show' : ''}`}
          onClick={closeMobileMenu}
        />

        {/* Mobile menu overlay */}
        <div className="d-lg-none">
          <div
            className={`stash-header__mobile-menu ${showMobileMenu ? 'stash-header__mobile-menu--show' : ''}`}
          >
            {/* Mobile menu header with logo and close */}
            <div className="stash-header__mobile-menu-header">
              <Link to="/" onClick={closeMobileMenu} className="pe-5">
                <img
                  src={logoDark}
                  alt="Stash House"
                  className="stash-header__mobile-menu-logo img-fluid"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Link>
              <button
                className="stash-header__mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <span className="stash-header__mobile-menu-close-line"></span>
                <span className="stash-header__mobile-menu-close-line"></span>
              </button>
            </div>

            <div className="stash-header__mobile-menu-content">
              {/* Welcome message */}
              {user && (
                <div className="stash-header__mobile-menu-welcome ">
                  Welcome back, <span className="text-stash-accent">{getUserDisplayName()}</span>!
                </div>
              )}

              {/* Shop section - hidden for brand users */}
              {!isBrandUser() && (
                <div className="stash-header__mobile-menu-section">
                  <h6 className="stash-header__mobile-menu-section-title">Shop</h6>
                  <ul className="stash-header__mobile-nav">
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/brands"
                        className="stash-header__mobile-nav-link"
                        onClick={closeMobileMenu}
                      >
                        Brands
                      </Link>
                    </li>
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/categories"
                        className="stash-header__mobile-nav-link"
                        onClick={closeMobileMenu}
                      >
                        Categories
                      </Link>
                    </li>
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/products?filter=new"
                        className="stash-header__mobile-nav-link"
                        onClick={closeMobileMenu}
                      >
                        New Arrivals
                      </Link>
                    </li>
                    {!isEmployee() && (
                      <li className="stash-header__mobile-nav-item">
                        <Link
                          to="/quick-order"
                          className="stash-header__mobile-nav-link stash-header__mobile-nav-link--accent"
                          onClick={closeMobileMenu}
                        >
                          Quick Order
                        </Link>
                      </li>
                    )}
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/products?filter=sale"
                        className="stash-header__mobile-nav-link stash-header__mobile-nav-link--accent"
                        onClick={closeMobileMenu}
                      >
                        On Sale
                      </Link>
                    </li>
                  </ul>
                </div>
              )}

              {/* Account section */}
              <div className="stash-header__mobile-menu-section">
                <h6 className="stash-header__mobile-menu-section-title">Account</h6>
                <ul className="stash-header__mobile-nav">
                  <li className="stash-header__mobile-nav-item">
                    <Link
                      to="/account"
                      className="stash-header__mobile-nav-link"
                      onClick={closeMobileMenu}
                    >
                      <i className="bi bi-person-circle"></i>
                      My Account
                    </Link>
                  </li>
                  {!isEmployee() && (
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/orders"
                        className="stash-header__mobile-nav-link"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-box-seam"></i>
                        Orders
                      </Link>
                    </li>
                  )}
                  {!isBrandUser() && (
                    <li className="stash-header__mobile-nav-item">
                      <Link
                        to="/lab-reports"
                        className="stash-header__mobile-nav-link"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-file-earmark-medical"></i>
                        Labs
                      </Link>
                    </li>
                  )}
                  <li className="stash-header__mobile-nav-item">
                    <button
                      className="stash-header__mobile-nav-link stash-header__mobile-nav-link--danger"
                      onClick={handleLogout}
                    >
                      <i className="bi bi-box-arrow-right"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>

              {/* Sales Rep section */}
              {currentStore?.salesRep && (
                <div className="stash-header__mobile-menu-section">
                  <h6 className="stash-header__mobile-menu-section-title">Your Sales Rep</h6>
                  <div className="stash-header__mobile-sales-rep px-3 pb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-person-badge text-primary fs-4"></i>
                      <strong>{currentStore.salesRep.name}</strong>
                    </div>
                    {currentStore.salesRep.email && (
                      <div className="mb-1 ms-4">
                        <a href={`mailto:${currentStore.salesRep.email}`} className="small text-decoration-none d-flex align-items-center gap-2">
                          <i className="bi bi-envelope"></i>
                          {currentStore.salesRep.email}
                        </a>
                      </div>
                    )}
                    {currentStore.salesRep.phone && (
                      <div className="ms-4">
                        <a href={`tel:${currentStore.salesRep.phone}`} className="small text-decoration-none d-flex align-items-center gap-2">
                          <i className="bi bi-telephone"></i>
                          {currentStore.salesRep.phone}
                        </a>
                      </div>
                    )}
                    
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Component */}
        <Navigation isHeaderScrolled={isScrolled} />
      </header>

      {/* Cart Offcanvas - hidden for employees */}
      {!isEmployee() && <CartOffcanvas />}
      {!isEmployee() && <BackOrderOffcanvas />}
    </>
  );
};

export default Header;